import { useQueryClient } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import AllowanceTabs, { type AllowanceTab } from 'components/quoting/quotes/flat_allowances/AllowanceTabs'
import { Button, IconButton } from 'slab'
import { calculateAgeAtDate, convertGrapeMoneyToNumber, formatMoney } from 'utils'
import { getEmployeePlanParams } from 'utils/quote_calculations'
import {
  getGetQuotingQuoteResultsQuoteIdFlatAllowanceResultsQueryKey as getFlatAllowanceResultsQueryKey,
  getGetQuotingQuoteResultsQuoteIdRecommendedBenchmarkResultsQueryKey as getRecommendedBenchmarkResultsQueryKey,
  useGetQuotingQuoteResultsQuoteIdPlans as useGetPlans,
  usePutQuotingProspectsProspectIdCensusCensusIdEmployeesId as useUpdateProspectEmployee,
} from 'utils/web_api'
import { type MemberOnboardingMedicalPlanDisplayItem } from 'utils/web_api.schemas'

import { useQuote } from '../QuoteProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { EmployeeDetailsTab } from './EmployeeDetailsTab'
import { type PlanFiltersState } from './EmployeePlanFilters'
import { PlansTabContent } from './PlansTabContent'
import { useSelfServeExplorerContext } from './QuoteSelfServeExplorerContext'

interface EmployeeDetailsProps {
  employeeId: string
  onBackClick: () => void
}

export const EmployeeQuoteCard = ({ employeeId, onBackClick }: EmployeeDetailsProps) => {
  const [filters, setFilters] = useState<PlanFiltersState>({
    metalTiers: [],
    carriers: [],
    planTypes: [],
    onlyFullyCovered: false,
  })

  const {
    quoteId,
    getMonthlyAllowanceForEmployee,
    getEmployeeById,
    isEmployeeStateUsingProjectedData,
    getPlanAvailabilityForEmployee,
  } = useSelfServeExplorerContext()
  const { quote, quoteYear } = useQuote()

  const queryClient = useQueryClient()
  const monthlyAllowance = getMonthlyAllowanceForEmployee(employeeId)
  const employee = getEmployeeById(employeeId)
  if (!employee) return null
  const isUsingProjectedData = isEmployeeStateUsingProjectedData(employee)

  const { mutate: updateEmployeeMutation } = useUpdateProspectEmployee({
    mutation: {
      onError: () => {
        toast.error('Failed to update employee eligibility')
      },
      onSuccess: () => {
        toast.success('Employee eligibility updated')
        queryClient.invalidateQueries({ queryKey: getRecommendedBenchmarkResultsQueryKey(quoteId) })
        queryClient.invalidateQueries({ queryKey: getFlatAllowanceResultsQueryKey(quoteId) })
      }
    }
  })

  const toggleEligibility = () => {
    if (!quote?.quoting_prospect_id || !quote?.quoting_prospect_employee_census_id) {
      toast.error('Unable to update employee eligibility')
      return
    }

    updateEmployeeMutation({
      prospectId: quote.quoting_prospect_id,
      censusId: quote.quoting_prospect_employee_census_id,
      id: employee.id,
      data: {
        eligible: !employee.eligible
      },
    })
  }

  const planParams = getEmployeePlanParams(employee, monthlyAllowance)
  const { data: plans, isLoading: plansLoading, error: plansError } = useGetPlans(
    quoteId,
    planParams,
    {
      query: {
        refetchOnWindowFocus: false,
      },
    }
  )

  const planAvailabilityForEmployee = useMemo(() => getPlanAvailabilityForEmployee(employee), [employee, getPlanAvailabilityForEmployee])

  const hasPlansError = Boolean(plansError)

  const employeeAge = calculateAgeAtDate(employee.date_of_birth, new Date())
  const isMedicareEligible = employeeAge >= 65

  const filteredPlans = useMemo(() => {
    if (!plans || !plans.plan_items) return []

    return plans.plan_items.filter((plan: MemberOnboardingMedicalPlanDisplayItem) => {
      if (filters.metalTiers.length > 0 && !filters.metalTiers.includes(plan.plan.metal_tier)) {
        return false
      }

      if (filters.carriers.length > 0 && !filters.carriers.includes(plan.plan.carrier_name)) {
        return false
      }

      if (filters.planTypes.length > 0 && !filters.planTypes.includes(plan.plan.plan_type)) {
        return false
      }

      if (filters.onlyFullyCovered) {
        const planPremium = convertGrapeMoneyToNumber(plan.total_premium)
        if (planPremium > monthlyAllowance) {
          return false
        }
      }

      return true
    })
  }, [plans, filters, monthlyAllowance])

  const tabs: AllowanceTab[] = [
    {
      id: 'plans',
      label: isUsingProjectedData ? 'Example plans' : 'Plans Available',
      content: (
        <PlansTabContent
          employee={employee}
          plans={plans?.plan_items}
          plansLoading={plansLoading}
          hasPlansError={hasPlansError}
          filters={filters}
          setFilters={setFilters}
          filteredPlans={filteredPlans}
          monthlyAllowance={monthlyAllowance}
          usingProjectedData={isUsingProjectedData}
          planAvailabilityForEmployee={planAvailabilityForEmployee}
          quoteYear={quoteYear}
        />
      )
    },
    {
      id: 'details',
      label: 'Employee Details',
      content: <EmployeeDetailsTab employee={employee} plans={plans?.plan_items} />,
      hidden: isMedicareEligible
    }
  ]

  return (
    <div className='bg-white border border-gray-200 rounded-lg animate-in slide-in-from-right duration-300'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between p-4 sm:p-6'>
        <div className='flex items-center gap-2'>
          <IconButton
            onClick={onBackClick}
            icon={ArrowLeft}
            label='Back to employees'
          />
          <div>
            <h2 className='heading-md sm:heading-lg text-gray-800 sensitive'>{employee.name}</h2>
          </div>
        </div>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center'>
          <Button
            variant='secondary'
            size='small'
            onClick={toggleEligibility}
          >
            {employee.eligible ? 'Make Ineligible' : 'Make Eligible'}
          </Button>
          <span className='px-3 py-1 rounded-full body border border-gray-200'>
            Monthly allowance: <span className='font-semibold'>{formatMoney(monthlyAllowance)}</span>
          </span>
        </div>
      </div>

      <AllowanceTabs
        defaultValue='plans'
        tabs={tabs}
        onValueChange={(value: string) => {
          if (value === 'plans') {
            emitEmployerOnboardingEvent('[ER] AllowanceTool see plans available clicked', {})
          } else if (value === 'details') {
            emitEmployerOnboardingEvent('[ER] AllowanceTool see employee details clicked', {})
          }
        }}
      />
    </div>
  )
}
